/**
 * Import Monitor — ERP Received tab.
 *
 * CR-7468. What the customer's order feed says it has received on a line,
 * beside what our receipts hold. The gap is the signal part two needs: an ERP
 * reporting a line finished short of its order is telling us the outstanding
 * quantity is not owed, and until this page there was nowhere to see whether
 * that signal is trustworthy per customer.
 *
 * Platform operators only — the server enforces it, and the tab is hidden from
 * everyone else. There is deliberately no second gate on whether a customer's
 * feed sends the figure: an empty table is itself the answer.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  erpReceivedAPI,
  type ErpReceivedDirection,
  type ErpReceivedFilters,
  type ErpReceivedResponse,
} from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Download, Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';

interface ImportErpReceivedTabProps {
  customerId: number;
}

const PAGE_SIZE = 25;

export default function ImportErpReceivedTab({ customerId }: ImportErpReceivedTabProps) {
  const { formatDate, formatNumber } = useFormatters();
  const [data, setData] = useState<ErpReceivedResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState<ErpReceivedDirection | ''>('');
  const [page, setPage] = useState(1);

  const filters: ErpReceivedFilters = {
    search: search || undefined,
    direction: direction || undefined,
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await erpReceivedAPI.list(customerId, {
        search: search || undefined,
        direction: direction || undefined,
      }, page, PAGE_SIZE));
    } catch (err: unknown) {
      console.error('ERP received load failed:', err);
      setError('Failed to load ERP received figures');
      toast.error('Failed to load ERP received figures');
    } finally {
      setLoading(false);
    }
  }, [customerId, search, direction, page]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await erpReceivedAPI.exportXlsx(customerId, filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `erp_received_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel exported successfully');
    } catch (err: unknown) {
      console.error('ERP received export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;
  const summary = data?.summary;
  const qty = (v: string | null) => (v === null ? '—' : formatNumber(parseFloat(v)));

  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold">ERP Received</div>
        <div className="text-xs text-muted-foreground">
          What this customer&apos;s order feed reports it has received, against the receipts we hold.
          Platform operators only.
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Lines the feed states</p>
            <p className="text-2xl font-semibold tabular-nums">{summary.total.toLocaleString()}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Agree</p>
            <p className="text-2xl font-semibold tabular-nums text-green-600">{summary.agree.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Same figure both sides</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">ERP higher</p>
            <p className="text-2xl font-semibold tabular-nums text-blue-600">{summary.erp_higher.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Their books hold receipts ours do not</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">Helix higher</p>
            <p className="text-2xl font-semibold tabular-nums text-orange-600">{summary.helix_higher.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">We hold receipts they have not reported</p>
          </CardContent></Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            className="pl-8 pr-3 py-2 text-sm border rounded-md w-64"
            placeholder="PO, PO/line or part…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { setSearch(searchInput.trim()); setPage(1); }
            }}
          />
        </div>
        <select
          className="text-sm border rounded-md py-2 px-2"
          aria-label="Direction"
          value={direction}
          onChange={(e) => { setDirection(e.target.value as ErpReceivedDirection | ''); setPage(1); }}
        >
          <option value="">Every line</option>
          <option value="agree">Agree</option>
          <option value="erp_higher">ERP higher</option>
          <option value="helix_higher">Helix higher</option>
        </select>
        <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} aria-label="Refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || !data || data.count === 0} className="gap-2">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-red-600">{error}</div>
          ) : !data || data.results.length === 0 ? (
            /* The empty state is an answer, not a dead end: a customer whose
               feed never states a received quantity belongs here reading zero,
               and an operator needs to know that is what they are looking at. */
            <div className="py-12 px-6 text-center">
              <p className="text-sm font-medium">No line on this customer&apos;s book carries an ERP received figure.</p>
              <p className="text-sm text-muted-foreground mt-1">
                The order feed fills these in as it pushes. A customer whose feed does not send one
                stays empty here, and that is the finding.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold text-muted-foreground">PO / Line</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground">Part</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground">Supplier</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground text-right">Ordered</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground text-right">ERP received</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground text-right">Receipts</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground text-right">Accepted</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground text-right">Gap</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground">Reported</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((row) => {
                    const gap = parseFloat(row.difference) || 0;
                    return (
                      <tr key={row.po_line_id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <div className="font-medium">{row.po_number}</div>
                          <div className="text-xs text-muted-foreground">Line {row.line_number} · {row.status}</div>
                        </td>
                        <td className="px-4 py-3">{row.part_number || '—'}</td>
                        <td className="px-4 py-3">{row.supplier_name || '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{qty(row.ordered_qty)}</td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium">{qty(row.erp_received_qty)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {row.no_receipts ? (
                            /* Not a disagreement. The deliveries on these lines
                               happened before the receipt history we hold, so
                               there is nothing on our side to compare — saying
                               "0" here would read as a contradiction that is
                               really an absence (R-609). */
                            <span className="text-xs text-muted-foreground">no receipts held</span>
                          ) : qty(row.gross_received)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{row.no_receipts ? '—' : qty(row.accepted_qty)}</td>
                        <td className={`px-4 py-3 text-right tabular-nums font-medium ${
                          row.no_receipts ? 'text-muted-foreground'
                            : gap > 0 ? 'text-blue-600'
                            : gap < 0 ? 'text-orange-600'
                            : 'text-muted-foreground'}`}>
                          {row.no_receipts ? '—' : qty(row.difference)}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {row.erp_reported_at ? formatDate(row.erp_reported_at) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {data && data.count > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {data.page} of {totalPages} · {data.count.toLocaleString()} lines
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading}
              onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading}
              onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
